import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LowStimProvider } from "@/contexts/LowStimContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ROLE_HOME } from "@/lib/sensory";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import StudentLogin from "@/pages/StudentLogin";
import TherapistDashboard from "@/pages/TherapistDashboard";
import StudentDetail from "@/pages/StudentDetail";
import ChooseEntry from "@/pages/ChooseEntry";
import FeelingsHome from "@/pages/FeelingsHome";
import FeelingScreen from "@/pages/FeelingScreen";
import StudentBoxes from "@/pages/StudentBoxes";
import StudentBox from "@/pages/StudentBox";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import Terms from "@/pages/legal/Terms";
import Accessibility from "@/pages/legal/Accessibility";
import EvidenceBase from "@/pages/legal/EvidenceBase";
import About from "@/pages/legal/About";

function RoleHomeRedirect() {
  const { loading } = useAuth();
  if (loading) return <div className="p-10 text-[var(--sw-ink-soft)]">Loading…</div>;
  // Root URL always shows the generic public landing, regardless of auth state.
  // Signed-in users can jump to their workspace via the header shortcut.
  return <Landing />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LowStimProvider>
          <Toaster position="top-right" richColors closeButton />
          <Layout>
            <Routes>
              <Route path="/" element={<RoleHomeRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/student-login" element={<StudentLogin />} />

              <Route path="/therapist" element={<ProtectedRoute kinds={["therapist"]}><TherapistDashboard /></ProtectedRoute>} />
              <Route path="/therapist/students/:id" element={<ProtectedRoute kinds={["therapist"]}><StudentDetail /></ProtectedRoute>} />

              {/* Student (curated set) */}
              <Route path="/play" element={<ProtectedRoute kinds={["student"]}><ChooseEntry mode="student" /></ProtectedRoute>} />
              <Route path="/play/feelings" element={<ProtectedRoute kinds={["student"]}><FeelingsHome mode="student" /></ProtectedRoute>} />
              <Route path="/play/feelings/:feeling" element={<ProtectedRoute kinds={["student"]}><FeelingScreen mode="student" /></ProtectedRoute>} />
              <Route path="/play/boxes" element={<ProtectedRoute kinds={["student"]}><StudentBoxes mode="student" /></ProtectedRoute>} />
              <Route path="/play/boxes/:system" element={<ProtectedRoute kinds={["student"]}><StudentBox mode="student" /></ProtectedRoute>} />

              {/* Self-serve / explore (no auth) */}
              <Route path="/explore" element={<ChooseEntry mode="explore" />} />
              <Route path="/explore/feelings" element={<FeelingsHome mode="explore" />} />
              <Route path="/explore/feelings/:feeling" element={<FeelingScreen mode="explore" />} />
              <Route path="/explore/boxes" element={<StudentBoxes mode="explore" />} />
              <Route path="/explore/boxes/:system" element={<StudentBox mode="explore" />} />

              {/* Static / legal */}
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="/evidence" element={<EvidenceBase />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </LowStimProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
